import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import {
  PLATFORM_LABELS,
  SOCIAL_SOURCE_LINK_KINDS,
  SOURCE_LINK_KIND_LABELS,
  addDays,
  campaignCategoryLabel,
  formatCents,
  utcDay,
  type CampaignDetailResponse,
  type CampaignSeriesResponse,
  type EligibilityReasonCode,
  type LeaderboardResponse,
  type PublicCampaign,
  type SourceLink,
  type SourceLinkKind,
} from '@nexus/shared';

import { Estimator } from '../components/Estimator';
import { CampaignLibrarySection } from './CampaignLibrary';
import { ApiError, apiGet, apiSend } from '../lib/api';
import {
  Alert,
  AreaChart,
  Badge,
  Button,
  Card,
  ExternalLink,
  Fact,
  Facts,
  Glyph,
  LoadErrorPage,
  LoadingPage,
  Medal,
  Meter,
  Money,
  Num,
  PageHeader,
  Stat,
  StatGrid,
  When,
  formatNumber,
  type GlyphName,
} from '../ui';
import { budgetTone, platformName, timeLeft } from './Campaigns';

/**
 * One campaign, in full.
 *
 * ## Why this page exists
 *
 * Everything here used to be on a card in a list of cards, which made /campaigns
 * a page you scrolled rather than a page you searched. The list is now a grid of
 * compact cards and this is where a creator lands when they pick one — so this
 * page carries every fact the old card did, and the Join is here rather than
 * there for a reason that is not layout:
 *
 * `CampaignParticipation.documentHash` is meant to say "these are the words this
 * creator agreed to". The button that posts that hash therefore has to sit on a
 * page where the words are on screen, which a compact card in a grid cannot be.
 * The frozen terms are rendered open, immediately above the button.
 *
 * ## What the campaign-parity pass added
 *
 * The things every campaign page in this market carries and this one did not:
 * a rate PER PLATFORM (a table, when the figures differ), the most one clip can
 * earn, the client's own channels and documents as "Reference materials", the
 * top three with medals, and how the campaign is going over time. On an
 * apply-first campaign the button says Apply and the page says what happened
 * to the application.
 *
 * ## Layout
 *
 * Two columns on a desktop: the decision on the left (the figures, the rules,
 * what you are agreeing to, the button) and the context on the right (the
 * campaign's own facts, how it is going, how the views are verified, what a clip
 * would be worth). One column under 900px.
 */

/** The reasons a creator can do something about, and where they do it. */
const REASON_ACTIONS: Partial<Record<EligibilityReasonCode, { to: string; label: string }>> = {
  no_connected_account: { to: '/connections', label: 'Connect an account' },
  no_account_on_allowed_platform: { to: '/connections', label: 'Connect an account' },
  account_age_unknown: { to: '/connections', label: 'Re-read the account' },
};

/** How many days the activity chart shows. Thirty, like the wallet's default. */
const SERIES_DAYS = 30;

export function CampaignDetail(): React.JSX.Element {
  const { slug = '' } = useParams<{ slug: string }>();
  const [campaign, setCampaign] = useState<PublicCampaign | null>(null);
  const [series, setSeries] = useState<CampaignSeriesResponse | null>(null);
  const [board, setBoard] = useState<LeaderboardResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [missing, setMissing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const body = await apiGet<CampaignDetailResponse>(`/campaigns/${encodeURIComponent(slug)}`);
      setCampaign(body.campaign);
      setMissing(false);
    } catch (err) {
      /* A 404 is not a failure to load, it is an answer — a campaign that ended,
         or a link somebody was sent after it was taken down — and it gets its own
         page rather than "could not load", which would invite a retry loop. */
      if (err instanceof ApiError && err.status === 404) setMissing(true);
      else setError('Could not load this campaign.');
    }
  }, [slug]);

  useEffect(() => {
    void load();
  }, [load]);

  /* The chart and the podium are context, not the decision: each loads on its
     own and a failure leaves its card off the page rather than taking the
     campaign down with it. */
  useEffect(() => {
    if (!campaign) return;
    const to = utcDay(new Date());
    const from = addDays(to, -(SERIES_DAYS - 1));
    let cancelled = false;
    void (async () => {
      try {
        const body = await apiGet<CampaignSeriesResponse>(
          `/campaigns/${encodeURIComponent(campaign.slug)}/series?from=${from}&to=${to}`,
        );
        /* Shape-checked at the boundary: a body without points is not a
           series, whatever the status code said. */
        if (!cancelled) setSeries(Array.isArray(body?.points) ? body : null);
      } catch {
        if (!cancelled) setSeries(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [campaign]);

  const joined = campaign?.participation?.status === 'JOINED';
  useEffect(() => {
    /* The board is for the people on the campaign, and the API says so with a
       403 to everybody else; asking would only be a request that fails. */
    if (!campaign || !joined) return;
    let cancelled = false;
    void (async () => {
      try {
        const body = await apiGet<LeaderboardResponse>(
          `/campaigns/${encodeURIComponent(campaign.slug)}/leaderboard`,
        );
        if (!cancelled) setBoard(Array.isArray(body?.entries) ? body : null);
      } catch {
        if (!cancelled) setBoard(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [campaign, joined]);

  async function join(): Promise<void> {
    if (!campaign?.terms) return;
    setBusy(true);
    setJoinError(null);
    try {
      /* The id AND the hash of the rules THIS page rendered. The Worker refuses a
         join naming a superseded version or a hash that does not match, which is
         what makes `documentHash` mean "the words this creator read". */
      await apiSend('POST', `/campaigns/${campaign.slug}/join`, {
        termsVersionId: campaign.terms.versionId,
        documentHash: campaign.terms.documentHash,
      });
      await load();
    } catch (err) {
      setJoinError(err instanceof ApiError ? err.message : 'Could not reach the API.');
    } finally {
      setBusy(false);
    }
  }

  if (missing) {
    return (
      <>
        <PageHeader
          eyebrow="Creator"
          title="No such campaign"
          lead="This campaign is not live. It may have ended, or the link may be wrong."
          actions={
            <Link className="btn btn-primary" to="/campaigns">
              Back to campaigns
            </Link>
          }
        />
        <Card>
          <p className="muted">
            Anything you have already submitted to it is unaffected and still on{' '}
            <Link to="/submissions">My submissions</Link>.
          </p>
        </Card>
      </>
    );
  }

  if (error && campaign === null) {
    return (
      <LoadErrorPage
        eyebrow="Creator"
        title="Campaign"
        heading="Could not load this campaign."
        onRetry={() => void load()}
      >
        <span>
          The campaign is served by the API and it did not answer. Nothing you have joined is
          affected.
        </span>
      </LoadErrorPage>
    );
  }

  if (campaign === null) return <LoadingPage eyebrow="Creator" title="Campaign" stats />;

  const eligibility = campaign.eligibility;
  const ineligible = eligibility !== undefined && !eligibility.eligible;
  const removed = campaign.participation?.status === 'REMOVED';
  const applied = campaign.participation?.status === 'APPLIED';
  const declined = campaign.participation?.status === 'DECLINED';
  const applyFirst = campaign.joinMode === 'APPLY';

  const requirements = [
    campaign.minFollowers > 0 ? `${formatNumber(campaign.minFollowers)} followers` : null,
    campaign.minAccountAgeDays > 0 ? `${campaign.minAccountAgeDays} days old` : null,
  ].filter(Boolean);

  const rates = campaign.platformRates;
  const distinctRates = [...new Set(rates.map((r) => r.ratePerThousandCents))];
  const uniform = distinctRates.length <= 1;
  const lowestRate = rates.length > 0 ? Math.min(...rates.map((r) => r.ratePerThousandCents)) : campaign.ratePerThousandCents;
  const highestRate = rates.length > 0 ? Math.max(...rates.map((r) => r.ratePerThousandCents)) : campaign.ratePerThousandCents;

  return (
    <>
      <PageHeader
        eyebrow={campaign.brand.displayName}
        title={campaign.name}
        lead={campaign.objective ?? undefined}
        actions={
          <>
            <Link className="btn btn-quiet" to="/campaigns">
              ← All campaigns
            </Link>
            {joined && (
              <Link className="btn btn-primary" to="/submit">
                Submit a clip
              </Link>
            )}
          </>
        }
      />

      <div className="split">
        <div className="stack">
          <Card variant="feature" label={`${campaign.name} — the offer`} tight>
            <div className="card-title-row">
              <span className="chip quiet">{campaignCategoryLabel(campaign.category)}</span>
              {applyFirst && !joined && (
                <span className="chip lock" data-testid="apply-first">
                  <Glyph name="lock" size={14} />
                  Apply first
                </span>
              )}
              {removed ? (
                <Badge tone="critical">Removed</Badge>
              ) : joined ? (
                <Badge tone="positive">Joined</Badge>
              ) : applied ? (
                <Badge tone="waiting">Applied</Badge>
              ) : declined ? (
                <Badge tone="critical">Declined</Badge>
              ) : eligibility ? (
                <Badge tone={eligibility.eligible ? 'positive' : 'waiting'}>
                  {eligibility.eligible ? 'Eligible' : 'Why not'}
                </Badge>
              ) : null}
            </div>

            {/* `dense`, so the four tiles sit on ONE row in this column rather
                than three with "Time left to post" orphaned under them with three
                empty cells beside it. */}
            <StatGrid dense>
              <Stat
                label="Rate"
                value={
                  uniform ? (
                    <Money cents={rates[0]?.ratePerThousandCents ?? campaign.ratePerThousandCents} />
                  ) : (
                    <>
                      <Money cents={lowestRate} />
                      <span className="unit">–{formatCents(highestRate)}</span>
                    </>
                  )
                }
                note={uniform ? 'per 1,000 verified views' : 'per 1,000 views — differs by platform'}
                emphasis
              />
              <Stat
                label="Budget left"
                value={<Money cents={campaign.budgetRemainingCents} />}
                note={`of ${formatCents(campaign.budgetCents)}`}
              />
              <Stat
                label="Your cap"
                value={
                  campaign.perCreatorCapCents === null ? (
                    <span className="muted">No cap</span>
                  ) : (
                    <Money cents={campaign.perCreatorCapCents} />
                  )
                }
                note={
                  campaign.perCreatorCapCents === null
                    ? 'Nothing limits what one creator earns here.'
                    : 'The most one creator can earn.'
                }
              />
              <Stat label="Time left to post" value={timeLeft(campaign)} />
            </StatGrid>

            {/**
             * WHAT IT PAYS, PER PLATFORM, when the figures differ — and the
             * per-clip ceiling beside them, because "$2.00 per thousand, up to
             * $50 a clip" is one sentence and not two facts a creator has to
             * find on two cards.
             */}
            {(!uniform || campaign.perSubmissionCapCents !== null) && (
              <table className="rate-table" data-testid="rate-table">
                <thead>
                  <tr>
                    <th scope="col">Platform</th>
                    <th scope="col" className="num">
                      Per 1,000 views
                    </th>
                    <th scope="col" className="num">
                      Most per clip
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rates.map((row) => (
                    <tr key={row.platform}>
                      <td className="platform">
                        <Glyph name={platformGlyph(row.platform)} size={16} />
                        {PLATFORM_LABELS[row.platform]}
                      </td>
                      <td className="num">
                        <Money cents={row.ratePerThousandCents} />
                      </td>
                      <td className="num">
                        {campaign.perSubmissionCapCents === null ? (
                          <span className="muted">No cap</span>
                        ) : (
                          <Money cents={campaign.perSubmissionCapCents} />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {campaign.perSubmissionCapCents !== null && (
              <p className="muted" data-testid="per-clip-cap">
                Up to {formatCents(campaign.perSubmissionCapCents)} per clip, whatever it reaches.
              </p>
            )}

            <Meter
              value={campaign.budgetRemainingCents}
              max={campaign.budgetCents}
              tone={budgetTone(campaign)}
              label={`${formatCents(campaign.budgetRemainingCents)} left of ${formatCents(campaign.budgetCents)}`}
            />

            {/* WHICH ACCOUNT QUALIFIES, by name. The floors pass if ANY connected
                account on an allowed platform clears them and a creator may hold
                five per platform — so "Eligible" on its own invites a clip posted
                from the 500-follower account, which the submission checks then
                refuse. */}
            {eligibility?.eligible && (eligibility.qualifyingHandles?.length ?? 0) > 0 && (
              <p className="muted" data-testid="qualifying-accounts">
                {eligibility.qualifyingHandles.length === 1
                  ? `Post from ${eligibility.qualifyingHandles[0]}: it is the account of yours that meets this campaign's floors.`
                  : `Post from ${eligibility.qualifyingHandles.join(' or ')} — those are the accounts of yours that meet this campaign's floors.`}
              </p>
            )}

            {ineligible && (
              <div className="alert" data-tone="waiting" role="note">
                <strong>Why you do not qualify yet</strong>
                <ul>
                  {eligibility.reasons.map((reason) => (
                    <li key={reason.code} data-reason={reason.code}>
                      {reason.message}{' '}
                      {/* A reason a creator can act on gets the way to act on it.
                          The rest are the operator's or the clock's, and those
                          honestly have no link. */}
                      {REASON_ACTIONS[reason.code] && (
                        <Link to={REASON_ACTIONS[reason.code]!.to}>
                          {REASON_ACTIONS[reason.code]!.label}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {removed && (
              <Alert tone="critical" title="You were removed from this campaign">
                <span>{campaign.participation?.removedReason ?? 'No reason was recorded.'}</span>
              </Alert>
            )}

            {applied && (
              <Alert tone="waiting" title="Applied, waiting on the operator">
                <span>
                  You applied
                  {campaign.participation?.appliedAt ? (
                    <>
                      {' '}
                      on <When value={campaign.participation.appliedAt} />
                    </>
                  ) : null}
                  . The operator looks at every application by hand; you will be able to submit
                  clips the moment yours is approved.
                </span>
              </Alert>
            )}

            {declined && (
              <Alert tone="critical" title="Your application was declined">
                <span>
                  {campaign.participation?.applicationDeclineReason ?? 'No reason was recorded.'}
                </span>
              </Alert>
            )}

            {/**
             * THE EXACT TEXT THE HASH IS OF, open, immediately above the button
             * that agrees to it. The card used to render the content rules and
             * post the hash of a DIFFERENT, larger document, so the hash was of
             * words nobody had been shown — the one thing it exists not to be.
             */}
            {campaign.terms && !joined && !applied && (
              <details className="reveal" open>
                <summary>What you are agreeing to (version {campaign.terms.version})</summary>
                <pre className="prose-block" data-testid="campaign-terms-text">
                  {campaign.terms.documentText}
                </pre>
              </details>
            )}

            {eligibility !== undefined && (
              <div className="actions">
                {joined ? (
                  <>
                    <Link className="btn btn-primary" to="/submit">
                      Submit a clip
                    </Link>
                    <span className="muted">
                      {campaign.participation?.acceptedAt ? (
                        <>
                          Joined on <When value={campaign.participation.acceptedAt} />, terms
                          version {campaign.participation.termsVersion ?? '—'}.
                        </>
                      ) : (
                        'You are on this campaign.'
                      )}
                    </span>
                  </>
                ) : removed || applied ? null : (
                  <>
                    <Button
                      variant="primary"
                      onClick={() => void join()}
                      disabled={busy || ineligible || campaign.terms === null}
                      title={ineligible ? 'You do not qualify for this campaign yet.' : undefined}
                    >
                      {busy
                        ? applyFirst
                          ? 'Applying…'
                          : 'Joining…'
                        : declined
                          ? 'Apply again'
                          : applyFirst
                            ? 'Apply'
                            : 'Join'}
                    </Button>
                    {campaign.terms === null ? (
                      <span className="muted">
                        This campaign has no published terms yet, so it cannot be joined.
                      </span>
                    ) : ineligible ? (
                      <span className="muted">Clear the reasons above and this opens up.</span>
                    ) : applyFirst ? (
                      <span className="muted">
                        The operator approves who joins this one. Applying accepts the rules
                        above.
                      </span>
                    ) : null}
                  </>
                )}
              </div>
            )}

            {joinError && <Alert tone="critical">{joinError}</Alert>}
          </Card>

          {/**
           * REFERENCE MATERIALS: the client's own channels first (what a creator
           * studies for tone before cutting anything), then what to cut from,
           * then the documents. Missing entirely when there are no links would
           * read, on a LIVE campaign, as a page that failed to load rather than
           * as a campaign the client has not finished setting up.
           */}
          {(campaign.sourceLinks.length > 0 || campaign.status === 'ACTIVE') && (
            <Card title="Reference materials" tight>
              {campaign.sourceLinks.length === 0 ? (
                <p className="muted">
                  Nothing to cut from yet — the client has not added source material.
                </p>
              ) : (
                <ReferenceMaterials links={campaign.sourceLinks} />
              )}
            </Card>
          )}
          {/**
           * CLIPS TO DOWNLOAD: the ready-made clips Forge cut for this campaign,
           * from the NAS through signed links, immediately beneath the reference
           * materials they belong with. Self-contained (routes/CampaignLibrary.tsx)
           * and only for a creator who has JOINED — an applicant on an apply-first
           * campaign is not a member yet, and the Worker refuses anyone else — so
           * the page knows nothing about the library and cannot be broken by it.
           */}
          {joined && <CampaignLibrarySection slug={campaign.slug} />}

          {campaign.contentRules && (
            <Card title="Content rules" tight>
              <pre className="prose-block">{campaign.contentRules}</pre>
            </Card>
          )}

          {/**
           * HOW THE VIEWS ARE COUNTED, before anybody joins.
           *
           * This is the question every complaint in this market is ultimately
           * about — whose number wins when the platform's app and the payout
           * disagree — and Nexus has an unusually good answer, which is worth
           * nothing if a creator only finds it after being paid less than they
           * expected. Every figure comes from the campaign's own columns, so it
           * cannot describe a different campaign's rules.
           */}
          <Card title="How your views are verified" tight>
            <p className="muted">
              Nexus reads your post's public view count directly from{' '}
              {campaign.allowedPlatforms.map((p) => platformName(p)).join(', ')} on a schedule —
              not from a screenshot, and never from a number you type in. Readings are taken at
              fixed ages from the moment the post went live, and the figure you are paid on is the
              one at {campaign.measurementWindowHours} hours.
            </p>
            <ul className="rows">
              <li>
                <span className="row-title">Every reading is shown to you</span>
                <span className="muted">
                  On <Link to="/submissions">My submissions</Link>, with the time it was taken and
                  the number it found — including the ones that failed, so a gap is never silent.
                </span>
              </li>
              <li>
                <span className="row-title">The post must stay up</span>
                <span className="muted">
                  For {campaign.requiredLiveDays} day{campaign.requiredLiveDays === 1 ? '' : 's'}{' '}
                  after it is posted. A clip that is deleted, made private or unlisted stops being
                  countable, because nobody can see it any more.
                </span>
              </li>
              <li>
                <span className="row-title">Then a short holdback</span>
                <span className="muted">
                  {campaign.holdbackDays} days, during which a handful of named checks run on the
                  shape of the view curve. If one of them stops your earning you are told which, in
                  that rule's own words and with its own numbers.
                </span>
              </li>
            </ul>
          </Card>
        </div>

        <div className="stack">
          <Card title="The campaign" tight>
            <Facts>
              <Fact label="Post on">
                {campaign.allowedPlatforms.map((p) => platformName(p)).join(', ') || '—'}
              </Fact>
              <Fact label="Your account must be">
                {requirements.length > 0 ? requirements.join(', ') : 'Nothing in particular'}
              </Fact>
              <Fact label="Joining">
                {applyFirst ? 'Apply first — the operator approves' : 'Open to anyone who qualifies'}
              </Fact>
              <Fact label="Views counted">{campaign.measurementWindowHours} hours after you post</Fact>
              <Fact label="Paid">{campaign.holdbackDays} days after that</Fact>
              <Fact label="Ends">
                {campaign.endsAt ? <When value={campaign.endsAt} /> : <span className="muted">Open</span>}
              </Fact>
            </Facts>
          </Card>

          {/**
           * HOW IT IS GOING. A rate and a budget describe an offer; these
           * describe whether the offer is working. Counted from the rows on
           * every read, so they cannot claim a crowd that is not there — and
           * drawn over the last thirty days, so a creator can see whether the
           * campaign is still moving or was busy a month ago.
           */}
          <Card title="How it is going" tight label="How it is going">
            <StatGrid dense>
              <Stat
                label="Creators on it"
                value={
                  <span className="with-glyph">
                    <Glyph name="people" size={16} />
                    <Num value={campaign.activity.creatorsJoined} />
                  </span>
                }
              />
              <Stat label="Clips submitted" value={<Num value={campaign.activity.clipsSubmitted} />} />
              <Stat label="Approved" value={<Num value={campaign.activity.clipsApproved} />} />
              <Stat label="Verified views" value={<Num value={campaign.activity.verifiedViews} />} />
            </StatGrid>
            {series && (
              <AreaChart
                points={series.points.map((p) => ({
                  key: p.date,
                  label: p.date,
                  value: p.views,
                  compare: p.submissions,
                }))}
                valueName="verified views"
                compareName="clips submitted"
                formatValue={(n) => formatNumber(n)}
                summary={`The last ${SERIES_DAYS} days on ${campaign.name}: ${formatNumber(series.totals.views)} verified views and ${formatNumber(series.totals.submissions)} clips submitted, from ${series.from} to ${series.to}.`}
                emptyNote="Nothing in the last 30 days yet. A clip appears here on the day it is submitted, and its views on the day they are verified."
              />
            )}
          </Card>

          <Card title="What a clip would be worth" tight>
            <Estimator
              ratePerThousandCents={campaign.ratePerThousandCents}
              platformRates={rates}
              perSubmissionCapCents={campaign.perSubmissionCapCents}
            />
          </Card>

          {joined && (
            <Card title="How you are doing" tight>
              {board && board.entries.length > 0 ? (
                <Podium board={board} />
              ) : (
                <p className="muted">
                  The leaderboard is for creators on this campaign, and it counts verified views
                  only.
                </p>
              )}
              <div className="actions">
                <Link className="btn btn-ghost" to="/leaderboard">
                  Open the leaderboard
                </Link>
              </div>
            </Card>
          )}
        </div>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Pieces                                                              */
/* ------------------------------------------------------------------ */

function platformGlyph(platform: string): GlyphName {
  return platform === 'TIKTOK' ? 'tiktok' : platform === 'INSTAGRAM' ? 'instagram' : 'youtube';
}

/** The glyph for a link's kind. One set, drawn in `ui/Glyph.tsx`. */
export function sourceLinkGlyph(kind: SourceLinkKind | string): GlyphName {
  switch (kind) {
    case 'YOUTUBE':
      return 'youtube';
    case 'INSTAGRAM':
      return 'instagram';
    case 'TIKTOK':
      return 'tiktok';
    case 'DOC':
      return 'doc';
    case 'VOD':
      return 'vod';
    case 'CHANNEL':
      return 'channel';
    case 'FILE':
      return 'file';
    default:
      return 'link';
  }
}

/**
 * The reference links, grouped: the client's own social channels, then what
 * to cut from, then the documents. Each group is a labelled list so a screen
 * reader is told what the three lists are for.
 */
function ReferenceMaterials({ links }: { links: SourceLink[] }): React.JSX.Element {
  const social = links.filter((l) => SOCIAL_SOURCE_LINK_KINDS.includes(l.kind));
  const docs = links.filter((l) => l.kind === 'DOC');
  const material = links.filter((l) => !SOCIAL_SOURCE_LINK_KINDS.includes(l.kind) && l.kind !== 'DOC');

  const group = (title: string, rows: SourceLink[]): React.JSX.Element | null =>
    rows.length === 0 ? null : (
      <div className="stack tight" key={title}>
        <h3 className="muted" style={undefined}>
          {title}
        </h3>
        <ul className="rows ref-rows" aria-label={title}>
          {rows.map((link) => (
            <li key={`${link.kind}-${link.url}`}>
              <span className="ref-icon">
                <Glyph name={sourceLinkGlyph(link.kind)} size={16} />
              </span>
              <span className="ref-body">
                <span className="row-title">
                  <ExternalLink href={link.url}>{link.label}</ExternalLink>
                </span>
                <span className="ref-kind">{SOURCE_LINK_KIND_LABELS[link.kind] ?? link.kind}</span>
              </span>
            </li>
          ))}
        </ul>
      </div>
    );

  return (
    <div className="stack">
      {group('The client', social)}
      {group('Cut from', material)}
      {group('Read first', docs)}
    </div>
  );
}

/** The top three, with medals — the same strip the leaderboard opens with. */
export function Podium({ board }: { board: LeaderboardResponse }): React.JSX.Element {
  const top = board.entries.filter((e) => e.rank <= 3).slice(0, 3);
  return (
    <ol className="podium" aria-label="Top three">
      {top.map((entry) => (
        <li key={entry.creatorId} data-rank={entry.rank}>
          <span className="podium-name">
            <Medal rank={entry.rank} />
            {entry.displayName ?? 'A creator'}
            {entry.isYou && <Badge tone="positive">you</Badge>}
          </span>
          <span className="podium-figure">
            <Num value={entry.verifiedViews} />
          </span>
          <span className="podium-note">
            verified views
            {entry.earningsCents !== null && <> · {formatCents(entry.earningsCents)} earned</>}
          </span>
        </li>
      ))}
    </ol>
  );
}
